import React, { useContext } from "react";

import AddBoxIcon from "@mui/icons-material/AddBox";
import Button from "@mui/material/Button";
import ConfirmDialog from "../General/ConfirmDialog";
import Grid from "@mui/material/Grid";
import { HomeAppContext } from "../Home";
import api from "../../Services/api";
import { errorManagement } from "../../Services/errorManagement";
import MyDataGrid from "../General/Pagination/MyDataGrid";
import Edit from "./Edit";
import Entry from "./Entry";
import Create from "./Create";
import dayjs from "dayjs";

export const periodLogs = [
  {
    label: "Current month",
    from: dayjs().startOf("month").startOf("day"),
    to: dayjs().endOf("month").endOf("day")
  },
  {
    label: "Last month",
    from: dayjs().subtract(1, "month").startOf("month").startOf("day"),
    to: dayjs().subtract(1, "month").endOf("month").endOf("day")
  }
];

const statusOptions = [
  {
    id: "true",
    text: "Active"
  },
  {
    id: "false",
    text: "Inactive"
  }
];

export default function List(props) {
  const { homeState, homeDispatch } = useContext(HomeAppContext);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [openCreate, setOpenCreate] = React.useState(false);
  const [openRemove, setOpenRemove] = React.useState(false);
  const [removeParams, setRemoveParams] = React.useState(false);
  const [currentEntry, setCurrentEntry] = React.useState(null);
  const [refresh, setRefresh] = React.useState(0);

  const isAdmin =
    homeState.userProfile.role === "Admin" ||
    homeState.userProfile.role === "PowerUser";

  const allowActions = isAdmin;

  const handleClickCreate = (event) => {
    setOpenCreate(true);
  };

  const removeEngine = (params) => {
    homeDispatch({
      type: "NOTIFICATION",
      data: { message: "Removing engine", loading: true }
    });
    api
      .delete(`/api/ai-engines/${params.id}`)
      .then((response) => {
        if (response.data) {
          homeDispatch({
            type: "NOTIFICATION",
            data: { message: "AI Engine deleted successfuly", type: "success" }
          });
        }
        refreshItems();
      })
      .catch((error) => {
        errorManagement.formErrors(error, homeDispatch);
      });
  };

  const tableOptions = {
    columns: [
      {
        name: "isActive",
        description: "",
        sortable: true,
        filter: {
          enabled: false
        },
        size: 1
      },
      {
        name: "Name",
        description: "Engine Name",
        sortable: true,
        filter: {
          enabled: false
        },
        size: 2
      },
      {
        name: "createdBy.fullName",
        description: "Created By",
        sortable: true,
        filter: {
          enabled: false
        },
        size: 2
      },
      {
        name: "ModifiedAt",
        description: "Modified Date",
        sortable: true,
        filter: {
          enabled: false
        },
        size: 1
      },
      {
        name: "APIUrl",
        description: "Url",
        sortable: true,
        filter: {
          enabled: false
        },
        size: 3
      },
      {
        name: "APIToken",
        description: "Token",
        sortable: false,
        filter: {
          enabled: false
        },
        size: 3
      },
      {
        name: "Actions",
        description: "",
        sortable: false,
        filter: {
          enabled: false
        },
        size: 1
      }
    ],
    defaultSort: [{ name: "ModifiedAt", order: "-" }],
    additionalFilters: [
      {
        name: "EngineName",
        description: "Engine Name",
        filter: {
          enabled: true,
          name: "NameLike",
          comparison: "@=*",
          type: "text",
          value: "",
          defaultIndex: 0,
          placeholder: "Filter engine name"
        },
        size: allowActions ? 4 : "auto"
      },
      {
        name: "IsActive",
        description: "Status",
        filter: {
          enabled: true,
          comparison: "==",
          type: "select", 
          value: statusOptions,
          defaultIndex: 0,
          allowAll: true
        },
        size: 2
      },
      {
        name: "CreatedBy",
        description: "User",
        filter: {
          enabled: true,
          comparison: "==",
          type: "autocomplete",
          options: {
            url: "/api/users/dropdown",
            multiple: false,
            liveOptions: false,
            checkbox: false
          }
        },
        size: 2
      },
      {
        name: "Period",
        description: "Modified Date",
        filter: {
          enabled: true,
          required: true,
          comparison: "==",
          type: "dateRange",
          defaultValue: 2,
          value: periodLogs,
          options: {
            addNullPeriod: true
          }
        },
        size: 3
      }
    ]
  };

  React.useEffect(() => {
    api
      .get("/api/login/authorized")
      .then((r) => {
        homeDispatch({
          type: "UPDATE_CURRENT_LOCATION",
          data: "AI Engines"
        });
      })
      .catch((error) => errorManagement.formErrors(error, homeDispatch));
  }, [homeDispatch]);

  const handleClickEdit = (event, entry) => {
    console.log(entry);
    setCurrentEntry(entry);
    setOpenEdit(true);
  };

  const handleClickRemove = (event, entry) => {
    setCurrentEntry(entry);
    setRemoveParams({ id: entry.id });
    setOpenRemove(true);
  };

  const cancelRemove = () => {
    setRemoveParams(null);
  };

  const refreshItems = () => {
    setRefresh(refresh + 1);
  };

  return (
    <React.Fragment>
      <Grid container spacing={2} justifyContent={"space-between"}>
        {allowActions && (
          <Grid item xs={12}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClickCreate}
              startIcon={<AddBoxIcon />}
            >
              New AI Engine
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          <MyDataGrid
            options={tableOptions}
            url={"/api/ai-engines"}
            refresh={refresh}
            label={"AI Engines"}
            renderItem={(item, index) => (
              <Entry
                key={index}
                entry={item}
                allowActions={allowActions}
                isAdmin={isAdmin}
                onEdit={handleClickEdit}
                onRemove={handleClickRemove}
              />
            )}
          />
        </Grid>
      </Grid>
      {allowActions && (
        <React.Fragment>
          <Edit
            open={openEdit}
            setOpen={setOpenEdit}
            entry={currentEntry}
            onEdit={refreshItems}
          />
          <Create
            open={openCreate}
            setOpen={setOpenCreate}
            onCreate={refreshItems}
          />
          {currentEntry && (
            <ConfirmDialog
              open={openRemove}
              setOpen={setOpenRemove}
              title={"Remove " + currentEntry.engineName + "?"}
              message={
                'Are you sure that you want to remove the AI Engine "' +
                currentEntry.engineName +
                '"' +
                "?"
              }
              onCloseSuccess={removeEngine}
              successParams={removeParams}
              onClose={cancelRemove}
            />
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}
